import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Fragment } from 'react';

import { formatCurrencyFromCents } from '@apps/web/components/admin/revenue/revenue_utils';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import { useAdminRevenueSummary } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/admin/revenue/')({
    head: () => ({
        meta: [
            {
                title: 'Revenue | Phoebe Admin',
            },
        ],
    }),
    component: AdminRevenuePage,
});

function AdminRevenuePage() {
    const router = useRouter();
    const { data: summary, isLoading } = useAdminRevenueSummary();
    const getAccountNavigation = (accountId: string) => {
        const routeOptions = {
            to: '/admin/revenue/$account-id',
            params: { 'account-id': accountId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => {
                void router.navigate(routeOptions);
            },
        };
    };

    return (
        <div className="flex h-full min-w-0 flex-col overflow-hidden">
            <div className="border-border min-w-0 border-b px-4 py-4 sm:px-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold">Revenue</h2>
                        <p className="text-muted-foreground text-sm">
                            {summary?.active_account_count ?? 0} active accounts
                        </p>
                    </div>
                    <Button asChild={true} variant="outline" className="gap-2">
                        <Link to="/admin/revenue/accounts">
                            View All Accounts
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Button>
                </div>
            </div>
            <div className="min-w-0 flex-1 overflow-y-auto">
                <div className="mx-auto flex w-full max-w-7xl min-w-0 flex-col gap-6 p-4 sm:p-6">
                    {isLoading ? (
                        <div className="text-muted-foreground flex h-32 items-center justify-center gap-2 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading revenue summary…</span>
                        </div>
                    ) : (
                        <Fragment>
                            <div className="grid w-full min-w-0 gap-4 md:grid-cols-3">
                                <SummaryCard
                                    title="Contracted ARR"
                                    value={formatCurrencyFromCents(
                                        summary?.total_contracted_arr_cents ??
                                            0,
                                    )}
                                />
                                <SummaryCard
                                    title="Pending ARR"
                                    value={formatCurrencyFromCents(
                                        summary?.total_pending_arr_cents ?? 0,
                                    )}
                                />
                                <SummaryCard
                                    title="Implemented ARR"
                                    value={formatCurrencyFromCents(
                                        summary?.total_live_arr_cents ?? 0,
                                    )}
                                />
                            </div>
                            <Card className="min-w-0">
                                <CardHeader>
                                    <CardTitle>
                                        Pending Implementation
                                    </CardTitle>
                                    <CardDescription>
                                        {summary?.pending_implementation_count ??
                                            0}{' '}
                                        accounts with ARR not yet live
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="min-w-0 space-y-3">
                                    {summary?.pending_implementation_accounts
                                        .length ? (
                                        summary.pending_implementation_accounts.map(
                                            (account) => (
                                                <button
                                                    key={account.id}
                                                    type="button"
                                                    className="hover:bg-muted flex w-full min-w-0 items-start justify-between gap-3 rounded-sm border p-3 text-left transition-colors"
                                                    {...getNavigableElementProps(
                                                        getAccountNavigation(
                                                            account.id,
                                                        ),
                                                    )}
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <div className="truncate font-medium">
                                                            {account.name}
                                                        </div>
                                                    </div>
                                                    <div className="shrink-0 text-right text-sm">
                                                        <div>
                                                            {formatCurrencyFromCents(
                                                                account.live_arr_cents,
                                                            )}{' '}
                                                            /{' '}
                                                            {formatCurrencyFromCents(
                                                                account.pending_arr_cents,
                                                            )}
                                                        </div>
                                                        <div className="text-muted-foreground text-xs">
                                                            live / pending
                                                        </div>
                                                    </div>
                                                </button>
                                            ),
                                        )
                                    ) : (
                                        <div className="text-muted-foreground flex h-24 items-center justify-center text-sm">
                                            No accounts pending implementation
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </Fragment>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryCard({
    title,
    value,
}: {
    readonly title: string;
    readonly value: string;
}) {
    return (
        <Card className="min-w-0">
            <CardHeader>
                <CardDescription>{title}</CardDescription>
                <CardTitle className="text-2xl">{value}</CardTitle>
            </CardHeader>
        </Card>
    );
}
